import { SegmentOption } from "components/designV2/segmentcontroller/segmentcontroller";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { isPaymentMethodValueValidOrEmpty } from "web-apps/company/components/staff/components/shared/staff.paymentMethod.logic";
import {
  DEFAULT_FORM,
  buildDiffPlan,
  buildInitialFormData,
  getActiveCategoryHasSettings,
  getCategoryOptions,
  getChangedSettings,
  getModuleSettingsForCategory,
  getVisibleSettingKeysForCategory,
  resetHiddenSettingsForDisabledModule,
  shouldReloadAfterSavingSettings,
  updateSavedSettingsForKeys,
} from "./companySystemSettings.config";
import {
  ChangedSetting,
  CompanySystemSettingsFormData,
  ModuleSettingItem,
  ModuleSettingKey,
  SystemSettingsCategory,
} from "./companySystemSettings.types";

const parseSystemSettingsCategory = (
  value: string | null,
): SystemSettingsCategory | null => {
  switch (value) {
    case "gigs":
    case "timeReports":
    case "salary":
    case "stampClock":
    case "tips":
    case "ob":
    case "other":
    case "bankAccount":
      return value;
    case "chat":
      return "other";
    case null:
      return null;
    default:
      return null;
  }
};

type UseCompanySystemSettingsResult = {
  activeCategory: SystemSettingsCategory;
  setActiveCategory: React.Dispatch<
    React.SetStateAction<SystemSettingsCategory>
  >;
  formData: CompanySystemSettingsFormData;
  setFormData: React.Dispatch<
    React.SetStateAction<CompanySystemSettingsFormData>
  >;
  madeChanges: boolean;
  categoryOptions: SegmentOption<SystemSettingsCategory>[];
  moduleSettings: ModuleSettingItem[];
  activeCategoryHasSettings: boolean;
  activeCategoryHasChanges: boolean;
  isLoading: boolean;
  isReady: boolean;
  isSaving: boolean;
  loadFailed: boolean;
  handleUpdate: () => void;
  retryLoad: () => void;
  setModuleToggle: (key: ModuleSettingKey) => (value: boolean) => void;
};

export const useCompanySystemSettings = (): UseCompanySystemSettingsResult => {
  const { t } = useTranslation();
  const location = useLocation();
  const [companyState, , companyDispatch] = useCompanyStore();
  const companyId = companyState.company?.id;
  const [, , alertDispatch] = useAlertStore();

  const [activeCategory, setActiveCategory] =
    useState<SystemSettingsCategory>("gigs");
  const [formData, setFormData] =
    useState<CompanySystemSettingsFormData>(DEFAULT_FORM);
  const [hasLoadedInitialSettings, setHasLoadedInitialSettings] =
    useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const loadRequestIdRef = useRef(0);
  const saveRequestIdRef = useRef(0);
  const savedSettingsRef = useRef<CompanySystemSettingsFormData>(DEFAULT_FORM);
  const translationRef = useRef(t);

  useEffect(() => {
    translationRef.current = t;
  }, [t]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedCategory = parseSystemSettingsCategory(
      params.get("category"),
    );

    if (requestedCategory && requestedCategory !== activeCategory) {
      setActiveCategory(requestedCategory);
    }
  }, [activeCategory, location.search]);

  const showAlert = useCallback(
    (
      status: "loadError" | "partialSuccess" | "success" | "updateError",
    ): void => {
      const translate = translationRef.current;

      alertDispatch({
        type: "SHOW_ALERT",
        payload:
          status === "success"
            ? {
                icon: "checkmark",
                title: translate("Alerts.Updated"),
                message: translate("System.CompanySystemSettingsUpdated"),
              }
            : {
                icon: "alert",
                title: translate("Alerts.OhNo"),
                message:
                  status === "loadError"
                    ? translate("System.CompanySystemSettingsNotLoaded")
                    : status === "partialSuccess"
                      ? translate(
                          "System.CompanySystemSettingsPartiallyUpdated",
                        )
                      : translate("System.CompanySystemSettingsNotUpdated"),
              },
      });
    },
    [alertDispatch],
  );

  const categoryOptions = useMemo(() => getCategoryOptions(t), [t]);

  const loadSettings = useCallback((): void => {
    if (!companyId) return;

    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    setHasLoadedInitialSettings(false);
    setIsLoading(true);
    setLoadFailed(false);

    Api()
      .company.getCompanySettings(companyId)
      .then((response) => {
        if (loadRequestIdRef.current !== requestId) return;

        const initialFormData = buildInitialFormData(response.data);
        companyDispatch({
          type: "GET_COMPANY_SETTINGS",
          payload: response.data,
        });
        savedSettingsRef.current = initialFormData;
        setFormData(initialFormData);
        setHasLoadedInitialSettings(true);
        setLoadFailed(false);
      })
      .catch(() => {
        if (loadRequestIdRef.current !== requestId) return;

        setLoadFailed(true);
        showAlert("loadError");
      })
      .finally(() => {
        if (loadRequestIdRef.current !== requestId) return;
        setIsLoading(false);
      });
  }, [companyDispatch, companyId, showAlert]);

  useEffect(() => {
    if (!companyId) return;
    loadSettings();
  }, [companyId, loadSettings]);

  useEffect(() => {
    saveRequestIdRef.current += 1;
    setIsSaving(false);
  }, [companyId]);

  useEffect(() => {
    return () => {
      loadRequestIdRef.current += 1;
      saveRequestIdRef.current += 1;
    };
  }, []);

  const diffPlan = useMemo(() => buildDiffPlan(), []);

  const changedSettings = useMemo<ChangedSetting[]>(
    () => getChangedSettings(formData, savedSettingsRef.current, diffPlan),
    [diffPlan, formData],
  );

  const madeChanges = changedSettings.length > 0;

  const visibleSettingKeysForActiveCategory = useMemo(
    () => getVisibleSettingKeysForCategory(activeCategory, formData),
    [activeCategory, formData],
  );

  const changedSettingsForActiveCategory = useMemo(
    () =>
      changedSettings.filter((setting) =>
        visibleSettingKeysForActiveCategory.includes(setting.key),
      ),
    [changedSettings, visibleSettingKeysForActiveCategory],
  );

  const handleUpdate = useCallback(() => {
    if (
      !companyId ||
      !hasLoadedInitialSettings ||
      isSaving ||
      (activeCategory === "bankAccount" &&
        !isPaymentMethodValueValidOrEmpty(
          formData.companyBankAccountType,
          formData.companyBankAccountValue,
        )) ||
      changedSettingsForActiveCategory.length === 0
    ) {
      return;
    }

    const requestId = saveRequestIdRef.current + 1;
    saveRequestIdRef.current = requestId;
    setIsSaving(true);

    Promise.allSettled(
      changedSettingsForActiveCategory.map((setting) =>
        Api()
          .company.systemSettings.updateCompanySetting(
            companyId,
            setting.key,
            setting.value,
          )
          .then(() => setting),
      ),
    )
      .then((results) => {
        if (saveRequestIdRef.current !== requestId) return;

        const successfulSettings = results.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        );

        successfulSettings.forEach((setting) => {
          companyDispatch({
            type: "UPDATE_COMPANY_SETTINGS",
            payload: { key: setting.key, value: setting.value },
          });
        });

        if (successfulSettings.length > 0) {
          savedSettingsRef.current = updateSavedSettingsForKeys(
            successfulSettings.map((setting) => setting.key),
            formData,
            savedSettingsRef.current,
          );
          setFormData((previous) => ({ ...previous }));
        }

        if (shouldReloadAfterSavingSettings(successfulSettings)) {
          loadSettings();
        }

        if (
          successfulSettings.length === changedSettingsForActiveCategory.length
        ) {
          showAlert("success");
          return;
        }

        if (successfulSettings.length > 0) {
          showAlert("partialSuccess");
          return;
        }

        showAlert("updateError");
      })
      .finally(() => {
        if (saveRequestIdRef.current !== requestId) return;
        setIsSaving(false);
      });
  }, [
    activeCategory,
    changedSettingsForActiveCategory,
    companyDispatch,
    companyId,
    formData,
    hasLoadedInitialSettings,
    isSaving,
    loadSettings,
    showAlert,
  ]);

  const isReady = hasLoadedInitialSettings && !loadFailed;

  const retryLoad = useCallback((): void => {
    if (!companyId || isLoading) {
      return;
    }

    loadSettings();
  }, [companyId, isLoading, loadSettings]);

  const activeCategoryHasChanges = changedSettingsForActiveCategory.length > 0;

  const activeCategoryHasSettings = useMemo(
    () => getActiveCategoryHasSettings(activeCategory),
    [activeCategory],
  );

  const moduleSettings = useMemo(
    () => getModuleSettingsForCategory(activeCategory, t),
    [activeCategory, t],
  );

  const setModuleToggle =
    (key: ModuleSettingKey) =>
    (value: boolean): void => {
      setFormData((previous) => {
        const nextFormData = { ...previous, [key]: value };

        if (value) {
          return nextFormData;
        }

        return resetHiddenSettingsForDisabledModule(
          key,
          nextFormData,
          savedSettingsRef.current,
        );
      });
    };

  return {
    activeCategory,
    setActiveCategory,
    formData,
    setFormData,
    madeChanges,
    categoryOptions,
    moduleSettings,
    activeCategoryHasSettings,
    activeCategoryHasChanges,
    isLoading,
    isReady,
    isSaving,
    loadFailed,
    handleUpdate,
    retryLoad,
    setModuleToggle,
  };
};
